import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {Label, RelativeTime} from '@primer/react'
import {type FC, useMemo} from 'react'

import type {CommentPreviewData} from './types'

interface FindingCommentPreviewProps {
  preview: CommentPreviewData
}

const severityVariant = (severity: string) => {
  switch (severity) {
    case 'high':
    case 'critical':
      return 'danger'
    case 'medium':
      return 'attention'
    default:
      return 'accent'
  }
}

export const FindingCommentPreview: FC<FindingCommentPreviewProps> = ({preview}) => {
  const {author, title, bodyHTML, severity, url, createdAt, diffHunk, suggestion} = preview

  const suggestionDiffEntries = suggestion?.diffEntries ?? []
  const diffHunkLines = useMemo(() => (diffHunk ? parseUnifiedDiff(diffHunk) : []), [diffHunk])

  return (
    <div className="d-flex flex-column gap-2" data-testid="finding-comment-preview">
      <div className="d-flex flex-items-center flex-wrap gap-2">
        <GitHubAvatar alt={`@${author.login}`} size={20} src={author.avatarUrl} />
        <span className="text-semibold text-small">
          {url ? (
            <a href={url} className="Link--primary">
              {author.login}
            </a>
          ) : (
            author.login
          )}
        </span>
        {severity && (
          <Label variant={severityVariant(severity)}>{severity.charAt(0).toUpperCase() + severity.slice(1)}</Label>
        )}
        {createdAt && (
          <span className="color-fg-muted text-small">
            <RelativeTime datetime={createdAt} />
          </span>
        )}
      </div>

      {title && <h3 className="f4 text-semibold m-0">{title}</h3>}

      {diffHunkLines.length > 0 && (
        <div data-testid="finding-comment-diff-hunk">
          <div className="d-flex flex-column overflow-hidden border rounded-2">
            <StaticUnifiedDiffPreview
              tabSize={4}
              className="m-0 overflow-hidden"
              diffTableClassName="borderColor-default"
              diffLines={diffHunkLines}
            />
          </div>
        </div>
      )}

      <div className="text-small markdown-body">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */}
        <VerifiedHTMLText html={bodyHTML as SafeHTMLString} />
      </div>

      {suggestionDiffEntries.map((entry, i) => (
        <div key={`${entry.path}-${i}`} className="d-flex flex-column gap-2" data-testid="finding-comment-suggestion">
          <h4 className="text-small text-semibold color-fg-muted m-0">Suggested change</h4>
          <div className="d-flex flex-column overflow-hidden border rounded-2">
            <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
              <span>{entry.path}</span>
            </div>
            <StaticUnifiedDiffPreview
              tabSize={4}
              className="m-0 border-bottom borderColor-default overflow-hidden"
              diffTableClassName="borderColor-default"
              diffLines={entry.diffLines}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
